import { defineStore } from 'pinia';
import { api } from '../boot/axios';
import { getRuntimeConfig } from '@erebus/runtime-config';
import { Platform } from 'quasar';
import { useActivationStore } from './activation';
import { useAuthStore } from './auth';
import { useCaseStore } from './case';
import { useClientStore } from './client';
import { useDepartmentStore } from './department';
import { useEmployerStore } from './employer';
import { useFormStore } from './form';
import { useLocationStore } from './location';
import { useMedicationStore } from './medication';
import { useMemberStore } from './member';
import { useMessageStore } from './message';
import { useStatsStore } from './stats';
import { useWorkflowStore } from './workflow';
import { useScheduleStore } from './schedule';
import { useFeedbackStore } from 'stores/feedback';
import { useIterationStore } from 'stores/iteration';
import { useFeatureStore } from 'stores/features';
import { useFeatureFlagsStore } from '@erebus/feature-flags';
import { useConnectionStore } from 'stores/connection';
import { useNotificationStore } from 'stores/notification';
import { useQualityStore } from 'stores/quality';
import { useAttachmentStore } from 'stores/attachment';
import { useEvacuationStore } from 'stores/evacuation';
import { LOGOUT_PENDING_KEY } from '../boot/logoutPending';

export const useApplicationStore = defineStore('application', {
  persist: true, //Need to do it false before running as it only takes saved state.

  state: () => ({
    clientCountry: '',
    branding: '',
    useGenericMeds: false,
    medServiceURL: null,
    syncing: false,
    dataRefreshing: false,
    serverVersion: null,
    deviceID: null,
    deployment: 'production',
    isElectron: false,
    viewingCase: false,
    qaDrawer: false,
    selectedCaseNumber: 0,
    showNotes: false,
    showCloseCaseDialog: false,
    closeCasePatientRecordID: null,
    caseReportOptions: {
      activationMessagingFlag: null,
      assignedDoctorID: null,
      caseType: [],
      clientID: null,
      clientLocationID: null,
      clientType: [],
      doctorSelection: [],
      endDate: null,
      includes: [],
      medicationUsed: [],
      patientRecordTags: [],
      output: [],
      selectedFields: [
        {
          name: 'patientRecordID',
          align: 'left',
          label: 'Case Number',
          field: 'patientRecordID',
          sortable: true,
        },
        {
          name: 'medications',
          align: 'left',
          label: 'Medications',
          field: 'medications',
          sortable: true,
        },
        {
          name: 'patientRecordDateCreated',
          align: 'left',
          label: 'Created Date',
          field: 'patientRecordDateCreated',
          sortable: true,
        },
        {
          name: 'closeDate',
          align: 'left',
          label: 'Close Date',
          field: 'closeDate',
          sortable: true,
        },
      ],
      soapNotes: [],
      startDate: null,
      systemCategory: null,
      treatmentRecommendation: [],
    },
    medicationsLogOptions: {
      startDate: null,
      endDate: null,
      clientID: null,
      clientLocationID: null,
    },
    telemedicineReportOptions: {
      startDate: null,
      endDate: null,
      clientID: null,
      clientLocationID: null,
    },
    prescriptionReportOptions: {
      startDate: null,
      endDate: null,
      clientID: null,
      clientLocationID: null,
      selectedFields: [],
    },
    medkitPrescriptionOptions: {
      startDate: null,
      endDate: null,
      clientLocationID: null,
      selectedFields: [],
      fields: [
        { label: 'Case Number', field: 'caseNumber' },
        { label: 'Medication', field: 'medicationName' },
        { label: 'Quantity', field: 'prescriptionQuantity' },
        { label: 'Doctor', field: 'doctorName' },
        { label: 'Dosage', field: 'prescriptionDosage' },
        { label: 'Prescription Date', field: 'prescriptionDate' },
      ],
    },
  }),

  getters: {
    /**
     * Returns the legacy Java API base URL from runtime config
     * Used by Footer.vue and other legacy components for display purposes
     * @deprecated Use legacyApiBaseURL for clarity
     */
    apiBaseURL: () => {
      try {
        const config = getRuntimeConfig();
        return config.legacyApiBaseUrl;
      } catch {
        // Fallback during initialization before config is loaded
        return window.location.origin;
      }
    },
    /**
     * Returns the legacy Java API base URL from runtime config
     * Used by Footer.vue and other components for display purposes
     */
    legacyApiBaseURL: () => {
      try {
        const config = getRuntimeConfig();
        return config.legacyApiBaseUrl;
      } catch {
        // Fallback during initialization before config is loaded
        return window.location.origin;
      }
    },
    /**
     * Returns the Erebus Go API base URL from runtime config
     */
    erebusApiBaseURL: () => {
      try {
        const config = getRuntimeConfig();
        return config.apiBaseUrl;
      } catch {
        // Fallback during initialization before config is loaded
        return null;
      }
    },
    getDeployment: () => {
      const origin = window.location.origin;
      if (origin.includes('localhost')) {
        return 'development';
      } else if (origin.includes('beta')) {
        return 'beta';
      } else if (origin.includes('praxes')) {
        return 'praxes';
      } else if (origin.includes('rmi')) {
        return 'rmi';
      }
      return 'unknown';
    },
    /**
     * Returns true if the user is both on a mobile device (or small screen) and viewing a case
     * @return {boolean}
     */
    isViewingCase: (state) => {
      // the intent here is to monitor if a case page is shown, and if so change nav icons
      const windowWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
      return (Platform.is.mobile || windowWidth < 600) && state.viewingCase;
    },
    /**
     * Backwards compatibility for components using appStore.isOnline
     * @return {boolean} true unless app is fully offline
     */
    isOnline() {
      const connectionStore = useConnectionStore();
      return connectionStore.isOnline;
    },
    /**
     * Backwards compatibility for medServiceOnline checks
     * @return {boolean} true unless erebus backend is offline
     */
    medServiceOnline() {
      const connectionStore = useConnectionStore();
      return connectionStore.erebusStatus !== 'offline';
    },
  },

  actions: {
    reset() {
      // We won't reset anything here because none of it is privacy related.
      // Object.assign(state, getDefaultState())
      this.showNotes = false;
      this.selectedCaseNumber = 0;

      // This is also the master method that calls reset everywhere else.
      // The logout-pending marker survives it, in both modes: it says a cookie
      // session still has to be ended, and a bearer page load (the referral
      // page) or a bearer logout must not forget that (PRX-1745).
      const logoutPending = localStorage.getItem(LOGOUT_PENDING_KEY);
      localStorage.clear();
      if (logoutPending !== null) {
        localStorage.setItem(LOGOUT_PENDING_KEY, logoutPending);
      }
      const activationStore = useActivationStore();
      activationStore.$reset();
      const authStore = useAuthStore();
      authStore.$reset();
      const caseStore = useCaseStore();
      caseStore.$reset();
      const clientStore = useClientStore();
      clientStore.$reset();
      const deptStore = useDepartmentStore();
      deptStore.$reset();
      const empStore = useEmployerStore();
      empStore.$reset();
      const featureStore = useFeatureStore();
      featureStore.$reset();
      const formStore = useFormStore();
      formStore.$reset();
      const locationStore = useLocationStore();
      locationStore.$reset();
      const iterationStore = useIterationStore();
      iterationStore.$reset();
      const medicationStore = useMedicationStore();
      medicationStore.$reset();
      const memberStore = useMemberStore();
      memberStore.$reset();
      const messageStore = useMessageStore();
      messageStore.$reset();
      const scheduleStore = useScheduleStore();
      scheduleStore.$reset();
      const statsStore = useStatsStore();
      statsStore.$reset();
      const workflowStore = useWorkflowStore();
      workflowStore.$reset();
      const qualityStore = useQualityStore();
      qualityStore.$reset();
      const attachmentStore = useAttachmentStore();
      attachmentStore.$reset();
      const evacuationStore = useEvacuationStore();
      evacuationStore.$reset();
      const connectionStore = useConnectionStore();
      connectionStore.cleanup();
    },
    /**
     * @deprecated No longer needed - config is loaded from /config.json at startup
     * Kept for backward compatibility, does nothing
     */
    async getServerInfo() {
      console.warn(
        'getServerInfo() is deprecated and does nothing. Config is loaded from /config.json at startup.',
      );
    },
    /**
     * @deprecated No longer needed - Erebus URL is set from runtime config
     * Kept for backward compatibility, does nothing
     */
    setMedServiceURL() {
      console.warn(
        'setMedServiceURL() is deprecated and does nothing. Erebus URL is set from runtime config.',
      );
    },
    /**
     * Load the global feature flags into their cache.
     *
     * Called during app boot (see main.ts) so that reloading the page with an
     * existing session still gets flags. dataRefresh() also fetches them, but
     * it only runs after a login or a manual refresh - on its own it leaves
     * every flag reading false for the rest of the session.
     *
     * Flags must never block boot, so a failure is logged and swallowed. The
     * caller keeps the safe default of an empty cache, where every flag is off.
     */
    async fetchFeatureFlags() {
      try {
        await useFeatureFlagsStore().fetchFlags(true);
      } catch (err) {
        console.error('Failed to fetch feature flags:', err);
      }
    },
    async dataRefresh() {
      this.dataRefreshing = true;

      const caseStore = useCaseStore();
      const clientStore = useClientStore();
      const formStore = useFormStore();
      const locationStore = useLocationStore();
      const medicationStore = useMedicationStore();
      const memberStore = useMemberStore();
      const feedbackStore = useFeedbackStore();
      const notificationStore = useNotificationStore();

      const featureFlagsStore = useFeatureFlagsStore();

      const results = await Promise.allSettled([
        caseStore.dataRefresh(),
        clientStore.dataRefresh(),
        formStore.dataRefresh(),
        locationStore.dataRefresh(),
        medicationStore.dataRefresh(),
        memberStore.dataRefresh(),
        feedbackStore.dataRefresh(),
        notificationStore.dataRefresh(),
        featureFlagsStore.fetchFlags(true),
      ]);

      this.dataRefreshing = false;

      let success = true;
      for (const result of results) {
        if (result.status === 'rejected') {
          success = false;
          break;
        }
      }

      return { success };
    },
    async getCountryCode() {
      try {
        const res = await fetch('https://api.country.is/');
        const data = await res.json();
        const { country } = data;
        this.clientCountry = country;
        return this.clientCountry;
      } catch (e) {
        console.log(e);
      }
    },
    setServer(newServer) {
      // commit(SET_SERVER, newServer)
      this.apiBaseURL = newServer;
      api.defaults.baseURL = newServer;
    },
    /**
     * Go online and sync data
     */
    goOnline() {
      this.syncing = true;
      const actStore = useActivationStore();
      const medStore = useMedicationStore();
      const messageStore = useMessageStore();
      const caseStore = useCaseStore();
      const iterationStore = useIterationStore();
      actStore.sync();
      medStore.sync();
      messageStore.sync();
      caseStore.sync();
      iterationStore.sync();

      this.syncing = false;
    },
    showCloseCaseDialogForCase(patientRecordID) {
      this.closeCasePatientRecordID = patientRecordID;
      this.showCloseCaseDialog = true;
    },
    hideCloseCaseDialog() {
      this.showCloseCaseDialog = false;
      this.closeCasePatientRecordID = null;
    },
  },
});
